import { Boxes, Check, Pin, PinOff, ShieldCheck } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { Badge, Button, cn, ConfirmDialog, Dialog, ErrorState, formatInt, Panel, Pressable, RelativeTime, REVEAL_GROUP, RevealSlot, Segmented, Select, toast, Tooltip, toUiError } from '@orgus/ui';
import { comparePackVersions, DISCIPLINE_PACKS } from '@orgus/protocol';
import type { DisciplinePack, DisciplinePin, Project } from '@orgus/protocol';
import { useDisciplineActions } from './actions.ts';

const KIND_TEXT: Record<DisciplinePack['kind'], string> = { org: 'Organisation', base: 'Base', focus: 'Focus', language: 'Language' };

interface PackGroup {
  id: string;
  title: string;
  description: string;
  kind: DisciplinePack['kind'];
  versions: DisciplinePack[];
  pin: DisciplinePin | undefined;
}

function PinDialog({ g, projects, onClose }: { g: PackGroup; projects: Project[]; onClose: () => void }) {
  const actions = useDisciplineActions();
  const [version, setVersion] = useState(g.pin?.version ?? g.versions[0]!.version);
  const [scope, setScope] = useState<'all' | 'some'>(g.pin && g.pin.scope !== 'all' ? 'some' : 'all');
  const [picked, setPicked] = useState<string[]>(g.pin && Array.isArray(g.pin.scope) ? g.pin.scope : []);
  const [enforce, setEnforce] = useState(g.pin?.enforce ?? false);
  const [busy, setBusy] = useState(false);
  // A refusal from the hub, said in the dialog: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await actions.pin({ pack: g.id, version, scope: scope === 'all' ? 'all' : picked, enforce });
      toast.success(`${g.id} pinned to ${version}`, enforce ? 'It applies on top of every repo in scope from the next check.' : 'Repos that extend it get this version from the next check.');
      onClose();
    } catch (e) {
      setError(toUiError(e, { what: 'The pack was not pinned.', next: 'Only an owner or admin can pin packs.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Pin ${g.id}`}
      description="Every repo uses this version within seconds."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Pin />} onClick={() => void submit()} loading={busy} disabled={scope === 'some' && !picked.length}>
            Pin version
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Select label="Version" value={version} onValueChange={setVersion} options={g.versions.map((p) => ({ value: p.version, label: p.version }))} />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-muted">Repos</span>
          <Segmented size="sm" label="Repos" value={scope} onChange={setScope} options={[{ value: 'all', label: 'Every repo' }, { value: 'some', label: 'Some repos' }]} />
          {scope === 'some' && (
            <ul className="flex max-h-40 flex-col overflow-auto rounded-md border border-border-subtle">
              {projects.map((p) => (
                <li key={p.id} className="border-b border-border-subtle last:border-b-0">
                  <Pressable
                    aria-pressed={picked.includes(p.id)}
                    onClick={() => setPicked((x) => (x.includes(p.id) ? x.filter((y) => y !== p.id) : [...x, p.id]))}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-fg hover:bg-hover"
                  >
                    {picked.includes(p.id) ? <Check className="size-3.5 text-accent" aria-hidden /> : <span className="size-3.5" aria-hidden />}
                    {p.name}
                  </Pressable>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-muted">How it applies</span>
          <Segmented
            size="sm"
            label="How it applies"
            value={enforce ? 'enforce' : 'version'}
            onChange={(v) => setEnforce(v === 'enforce')}
            options={[
              { value: 'version', label: 'Version only' },
              { value: 'enforce', label: 'Enforce on every repo', icon: <ShieldCheck /> },
            ]}
          />
          <span className="text-xs text-fg-subtle">{enforce ? 'Applied on top of each repo\'s own rulebook: the organisation\'s floor.' : 'Only repos whose rulebook extends this pack use it, at this version.'}</span>
        </div>
        {error != null && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

/** Built-in and organisation packs, their versions, and the pins that hold them across repos. */
export function PacksPanel({ packs, pins, projects }: { packs: DisciplinePack[]; pins: DisciplinePin[]; projects: Project[] }) {
  const actions = useDisciplineActions();
  const [pinning, setPinning] = useState<PackGroup | null>(null);
  const [unpinning, setUnpinning] = useState<PackGroup | null>(null);
  const groups = useMemo<PackGroup[]>(() => {
    const by = new Map<string, DisciplinePack[]>();
    for (const p of [...DISCIPLINE_PACKS, ...packs]) by.set(p.id, [...(by.get(p.id) ?? []), p]);
    return [...by.entries()].map(([id, vs]) => {
      const versions = [...vs].sort((a, b) => comparePackVersions(b.version, a.version));
      return { id, title: versions[0]!.title, description: versions[0]!.description, kind: versions[0]!.kind, versions, pin: pins.find((x) => x.pack === id) };
    });
  }, [packs, pins]);
  const names = new Map(projects.map((p) => [p.id, p.name]));
  const rowActions = (g: PackGroup) =>
    g.pin ? (
      <>
        <Button size="sm" variant="ghost" icon={<Pin />} onClick={() => setPinning(g)}>
          Change pin
        </Button>
        <Button size="sm" variant="ghost" icon={<PinOff />} onClick={() => setUnpinning(g)}>
          Unpin
        </Button>
      </>
    ) : (
      <Button size="sm" variant="ghost" icon={<Pin />} onClick={() => setPinning(g)}>
        Pin version
      </Button>
    );
  return (
    <Panel
      title="Rule packs"
      sub={
        <>
          {/* A phone keeps the quiet count; the split by source needs the width of a wider screen. */}
          <span className="sm:hidden">{formatInt(groups.length)}</span>
          <span className="max-sm:hidden">{`${DISCIPLINE_PACKS.length} built in, ${new Set(packs.map((p) => p.id)).size} from your organisation`}</span>
        </>
      }
      icon={<Boxes />}
      flush
    >
      <ul>
        {groups.map((g) => {
          const latest = g.versions[0]!;
          const about = g.kind === 'org' && latest.publishedBy ? `${g.description} ${latest.version} published by ${latest.publishedBy}${latest.extends?.length ? `, extends ${latest.extends.join(', ')}` : ''}.` : g.description;
          return (
            <li key={g.id} className={cn(REVEAL_GROUP, 'flex min-w-0 flex-col gap-2 px-4 py-2 hover:bg-hover sm:h-row-2line sm:flex-row sm:items-center sm:gap-3 sm:py-0')}>
              <span className="flex min-w-0 flex-1 flex-col">
                {/* A phone wraps the name, its pin and its description instead of cutting them; a wider row truncates with a tooltip. */}
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 sm:flex-nowrap">
                  <span className="min-w-0 break-words font-mono text-ui text-fg sm:truncate">{g.id}</span>
                  <Badge data-chip="" size="sm" variant="outline" className="shrink-0">
                    {KIND_TEXT[g.kind]}
                  </Badge>
                  <span className="shrink-0 text-caption text-fg-subtle num">{latest.version}</span>
                </span>
                {g.pin ? (
                  <span className="flex min-w-0 items-start gap-1 text-caption text-fg-subtle">
                    {/* One caption line tall, so the pin sits on the first line when the caption wraps. */}
                    <span className="flex h-4 shrink-0 items-center">
                      <Pin className="size-3 text-accent" aria-hidden />
                    </span>
                    <span className="min-w-0 sm:truncate">
                      Pinned to {g.pin.version} on {g.pin.scope === 'all' ? 'every repo' : g.pin.scope.map((id) => names.get(id) ?? id).join(', ')}
                      {g.pin.enforce ? ', enforced' : ''} by {g.pin.by} <RelativeTime ts={g.pin.at} />
                    </span>
                  </span>
                ) : (
                  <Tooltip content={about}>
                    <span tabIndex={-1} className="line-clamp-2 text-caption text-fg-subtle sm:truncate">
                      {g.description}
                    </span>
                  </Tooltip>
                )}
              </span>
              {g.id !== 'orgus/recommended' && (
                <>
                  {/* A phone shows the actions under the text; a wider screen shows them on hover or focus. */}
                  <span className="flex items-center gap-1 sm:hidden">{rowActions(g)}</span>
                  <RevealSlot className="hidden shrink-0 items-center gap-1 sm:flex">{rowActions(g)}</RevealSlot>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {pinning && <PinDialog g={pinning} projects={projects} onClose={() => setPinning(null)} />}
      {unpinning && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setUnpinning(null)}
          title={`Unpin ${unpinning.id}?`}
          description={unpinning.pin?.enforce ? 'Repos stop getting it on top of their own rules from the next check.' : 'Repos that extend it get its newest version from the next check.'}
          confirmLabel="Unpin pack"
          onConfirm={async () => {
            await actions.unpin(unpinning.id);
            toast.success(`${unpinning.id} unpinned`);
            setUnpinning(null);
          }}
        />
      )}
    </Panel>
  );
}
